import { FC, useRef, useEffect } from 'react'
import { GetServerSideProps } from 'next'
import { useRouter } from 'next/router'

import MainLayout from 'containers/main-layout/main-layout'
import SEOMetatags from 'components/parts/seo-metatags/seo-metatags'
import SEOBreadCrumbs from 'components/parts/seo-metatags/seo-breadcrumbs'
import SEOMain from 'components/parts/seo-metatags/seo-main'
import ScrollEventWrap from 'containers/scroll-event-wrap/scroll-event-wrap'
import IntroHeader from 'components/intro-alt/intro-header/intro-header'
import IntroWhyUs from 'components/intro-alt/intro-why-us/intro-why-us'
import NearestWebinars from 'components/intro-alt/nearest-webinars/nearest-webinars'
import SnowFall from 'components/parts/snow-fall/snow-fall'
import IntroSpacer from 'components/intro-alt/intro-spacer/intro-spacer'
import IntroMaterials from 'components/intro-alt/intro-materials/intro-materials'
import IntroAiBolit from 'components/intro-alt/intro-ai-bolit/intro-ai-bolit'
import IntroClincase from 'components/intro-alt/intro-clincase/intro-clincase'
import IntroBottomBlock from 'components/intro-alt/intro-bottom-block/intro-bottom-block'
import SEOContext from 'context/SEOContext'
import Auth from 'store/auth'
import usePCWindow from 'hooks/usePCWindow'

import checkTokenExist from 'utils/checkTokenExist'
import nestRequest from 'utils/server-side/nestRequest'
import { concatParams, getParams, cutHashFromURL } from 'utils/getURLData'
import { MainUnregSEO } from 'constants/SEO'
import { MainPageBC } from 'constants/SEO-breadcrumbs'
import { popupName } from 'constants/auth/auth-data'
import { ScreenSize } from 'constants/media-constanst'
import { MAIN_PAGE_URL, StatusVocabulary } from 'constants/data'
import { A_MU_SCROLL } from 'utils/analytics/main_unauth/main_unauth'
import { useARegAndLogin, originReg } from 'utils/analytics/other/registration/registration'

import styles from 'styles/main.module.scss'

interface IMainPromoPage {
  cards: string
}

const MainPromoPage: FC<IMainPromoPage> = ({ cards }: IMainPromoPage) => {
  const { webinars, webinarsPast, article, video, podcast } = JSON.parse(cards)
  const containerRef = useRef<HTMLDivElement>(null)
  const { isAuthorized } = Auth.useAuth()
  const router = useRouter()
  const isMobile = usePCWindow(ScreenSize.mobile)
  const path = cutHashFromURL(router.asPath)

  const showRegistrationForm = () => {
    router.push(`${path}#${popupName.registration}`, undefined, { shallow: true })
  }

  useARegAndLogin(originReg.INTRO)
  useEffect(() => {
    if (!isAuthorized) return
    if (router.asPath.includes('#registration')) return

    const params = getParams(path)
    const formedParams = concatParams(params)
    const url = formedParams ? `${MAIN_PAGE_URL}?${formedParams}` : MAIN_PAGE_URL

    setTimeout(() => {
      router.push(url)
    }, 500)
  }, [isAuthorized, path, router.asPath])

  return (
    <>
      <SEOMetatags data={MainUnregSEO} />
      <SEOBreadCrumbs data={MainPageBC} />
      <SnowFall />
      <SEOMain></SEOMain>
      <ScrollEventWrap containerRef={containerRef} event={A_MU_SCROLL}>
        <SEOContext.Provider value={MainUnregSEO}>
          <div ref={containerRef}>
            <MainLayout mainPage={true} noLines={true}>
              <div className={styles.bgGradient}>
                <IntroHeader isMobile={isMobile} showRegistrationForm={showRegistrationForm} />
                <IntroSpacer />
                <IntroWhyUs />
                <NearestWebinars webinars={webinars} />
                <IntroSpacer />
                <div className={styles.aiBolitWrap}>
                  <IntroAiBolit />
                </div>
                <IntroSpacer />
                <IntroMaterials
                  webinarsPast={webinarsPast}
                  article={article}
                  video={video}
                  podcast={podcast}
                />
                <IntroSpacer />
                <IntroClincase />
                <IntroSpacer />
                <IntroBottomBlock showRegistrationForm={showRegistrationForm} />
              </div>
            </MainLayout>
          </div>
        </SEOContext.Provider>
      </ScrollEventWrap>
    </>
  )
}

export const getServerSideProps: GetServerSideProps = async (context) => {
  const reqCookies = context.req.headers.cookie
  const isToken = checkTokenExist(reqCookies)
  const formedParams = concatParams(context.query)
  const url = formedParams ? `${MAIN_PAGE_URL}?${formedParams}` : MAIN_PAGE_URL

  if (isToken) {
    return {
      redirect: {
        destination: url,
        permanent: false,
      },
    }
  }

  const props = { webinars: [], webinarsPast: [], article: [], podcast: null, video: [] }
  const articleParams = {
    offset: 0,
    limit: 2,
    count: false,
    ArticleTypeId: 1,
  }

  const videoParams = {
    offset: 0,
    limit: 2,
    count: false,
    ArticleTypeId: 2,
  }

  const podcastParams = {
    offset: 0,
    limit: 2,
    count: false,
  }

  const webinarSearchParams = {
    limit: 4,
    StatusId: StatusVocabulary.FUTURE,
  }

  const webinarPastSearch = {
    limit: 2,
    StatusId: StatusVocabulary.PAST,
  }

  try {
    const webinarRequest = nestRequest(context, 'get', `/api/webinar`, webinarSearchParams)
    const webinarPastRequest = nestRequest(context, 'get', `/api/webinar`, webinarPastSearch)
    const articleRequest = nestRequest(context, 'get', `/api/article`, articleParams)
    const videoRequest = nestRequest(context, 'get', `/api/article`, videoParams)
    const podcastRequest = nestRequest(context, 'get', `/api/podcast`, podcastParams)

    const results = await Promise.all([
      webinarRequest,
      webinarPastRequest,
      articleRequest,
      videoRequest,
      podcastRequest,
    ])

    if (results.some((result) => result.status !== 200)) throw new Error('rest request error')

    props.webinars = results[0]?.data.rows || []
    props.webinarsPast = results[1]?.data.rows || []
    props.article = results[2]?.data.rows || []
    props.video = results[3]?.data.rows || []
    props.podcast = results[4]?.data.rows || []
  } catch (err) {
    console.log(err)
  }

  return {
    props: {
      cards: JSON.stringify(props),
    },
  }
}

export default MainPromoPage
